'use client'

import { useMemo } from 'react'

export type HomeBlockType = 'banner' | 'sale-products' | 'featured-products' | 'new-products' | 'blogs' | 'category' | 'all-products' | 'showcase'

export type NormalizedHomeBlock = {
  id: string
  type: HomeBlockType
  position: number
  enabled: boolean
  config: any
  products?: any[]
  blogs?: any[]
  title?: string
  description?: string
  viewAllUrl?: string
  visible: boolean
}

const TYPE_DEFAULTS: Partial<Record<HomeBlockType, { title?: string; viewAllUrl?: string; description?: string }>> = {
  'sale-products': { title: 'Хямдралтай бүтээгдэхүүн', description: 'Энэ долоо хоногийн экспресс хямдралууд!', viewAllUrl: '/product?category=sale' },
  'featured-products': { title: 'Онцлох бүтээгдэхүүн', viewAllUrl: '/product?category=featured' },
  'new-products': { title: 'Шинэ бүтээгдэхүүн', viewAllUrl: '/product?category=new' },
  'category': { title: 'Ангилал' },
  'showcase': { title: 'Шилдэг бараа' },
}

function isBlockVisible(block: any, subscription: any, vendor: any): boolean {
  if (block.type === 'banner') return !!subscription?.canUseBanner
  if (block.type === 'blogs') {
    if (!subscription?.canUseBlog) return false
    if (!vendor?.modules?.includes('blog')) return false
    return true
  }
  return true
}

function normalizeBlock(block: any, vendor: any, subscription: any): NormalizedHomeBlock {
  const defaults = TYPE_DEFAULTS[block.type as HomeBlockType] || {}
  let viewAllUrl = defaults.viewAllUrl
  if (block.type === 'category' && block.config?.categoryId) {
    viewAllUrl = `/product?category=${block.config.categoryId}`
  }
  return {
    id: block.id,
    type: block.type,
    position: block.position,
    enabled: block.enabled,
    config: block.config || {},
    products: block.products,
    blogs: block.blogs,
    title: block.config?.title || defaults.title,
    description: block.config?.description || defaults.description,
    viewAllUrl,
    visible: isBlockVisible(block, subscription, vendor),
  }
}

export function useHomeBlocks({ vendor, homeBlocks: homeBlocksProp, subscription }: { vendor: any; homeBlocks?: any[]; subscription?: any }) {
  return useMemo(() => {
    const raw = homeBlocksProp || vendor?.homeBlocks
    if (!raw) return [] as NormalizedHomeBlock[]
    return [...raw]
      .filter(b => b.enabled)
      .sort((a, b) => a.position - b.position)
      .map(b => normalizeBlock(b, vendor, subscription))
      .filter(b => b.visible)
  }, [homeBlocksProp, vendor, subscription])
}
